import React, { useState, useEffect } from "react";
import { User, Camera, X, Upload } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { CircularSkeleton } from "./common/LoadingSkeleton";
// Dynamic import to avoid initialization order issues

// Enhanced Input Validation & Sanitization
const sanitizeString = (input: string, maxLength: number = 200): string => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/[<>"'&]/g, '') // Remove dangerous characters
    .substring(0, maxLength)
    .trim();
};


// Enhanced Rate Limiting
class ProfilePictureRateLimiter {
  private attempts: Map<string, { count: number; lastAttempt: number }> = new Map();
  private readonly maxAttempts = 5; // 5 uploads per hour
  private readonly windowMs = 60 * 60 * 1000; // 1 hour

  isAllowed(userId: string): { allowed: boolean; error?: string } {
    const now = Date.now();
    const userAttempts = this.attempts.get(userId);

    if (!userAttempts) {
      this.attempts.set(userId, { count: 1, lastAttempt: now });
      return { allowed: true };
    }

    // Reset if window has passed
    if (now - userAttempts.lastAttempt > this.windowMs) {
      this.attempts.set(userId, { count: 1, lastAttempt: now });
      return { allowed: true };
    }

    // Check if within limits
    if (userAttempts.count >= this.maxAttempts) {
      return { 
        allowed: false, 
        error: 'Too many photo uploads. Please wait before trying again.' 
      };
    }

    // Increment count
    userAttempts.count++;
    userAttempts.lastAttempt = now;
    return { allowed: true };
  }

  clearAttempts(userId: string): void {
    this.attempts.delete(userId);
  }
}

const profilePictureRateLimiter = new ProfilePictureRateLimiter();


interface ProfilePictureProps {
  src?: string | null;
  alt?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
  fallbackIcon?: React.ReactNode;
  user?: any; // Firebase user object
  userId?: string; // Direct user ID for fetching profile
  editable?: boolean; // Whether the profile picture can be edited
  onPhotoUpdate?: (newPhotoUrl: string) => void; // Callback when photo is updated
}

const ProfilePicture: React.FC<ProfilePictureProps> = ({
  src,
  alt = "Profile",
  size = "md",
  className = "",
  fallbackIcon,
  user,
  userId,
  editable = false,
  onPhotoUpdate,
}) => {
  const { currentUser } = useAuth();
  const [profileData, setProfileData] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  // Determine which user to fetch profile for
  const targetUserId = userId || user?.uid || currentUser?.uid;
  const isCurrentUser = targetUserId === currentUser?.uid;

  // Convert null/empty src to undefined to avoid TypeScript errors
  // Priority: 1. Direct src prop, 2. Firestore photoURL (single source of truth), 3. Firebase Auth user photoURL
  // Only fallback to currentUser.photoURL if this IS the current user
  const imageSrc =
    src && src.trim() !== ""
      ? src
      : profileData?.photoURL && profileData.photoURL.trim() !== ""
      ? profileData.photoURL
      : user?.photoURL && user.photoURL.trim() !== ""
      ? user.photoURL
      : isCurrentUser &&
        currentUser?.photoURL &&
        currentUser.photoURL.trim() !== ""
      ? currentUser.photoURL
      : undefined;

  // Check if the image source is a Google profile picture URL
  const isGoogleProfilePicture =
    imageSrc && imageSrc.includes("googleusercontent.com");

  // For Google profile pictures, we might want to show a different fallback or handle them differently
  const shouldShowFallback =
    !imageSrc || imageLoadError || (isGoogleProfilePicture && imageLoadError);

  // Quiet debug logging

  // Reset image load error when image source changes
  useEffect(() => {
    setImageLoadError(false);
  }, [imageSrc]);

  // Handle escape key to close modal and prevent body scroll when open
  useEffect(() => {
    if (!showUploadMenu) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowUploadMenu(false);
      }
    };

    // Prevent body scroll when modal is open on mobile (preserve scrollbar width)
    if (window.innerWidth < 768) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showUploadMenu]);

  // Fetch profile data when userId or user changes
  useEffect(() => {
    let mounted = true;
    const fetchProfile = async () => {
      try {
        const { doc, getDoc } = await import("firebase/firestore");
        const { db, auth } = await import("../services/firebaseConfig");
        const uid = targetUserId || auth.currentUser?.uid;
        if (!uid || !mounted) return;
        
        const docRef = doc(db, "Users", uid);
        const snap = await getDoc(docRef);
        if (snap.exists() && mounted) {
          const data = snap.data();
          setProfileData(data);
          // Removed debug log to prevent excessive console output and sensitive data exposure
        }
      } catch (error) {
        // SECURITY: Removed console.error to prevent information leakage
      }
    };
    
    // Only fetch if we don't have src or user.photoURL
    if (!src && !user?.photoURL && targetUserId) {
      fetchProfile();
    }
    return () => { mounted = false; };
  }, [targetUserId, src, user?.photoURL]); // Fetch when userId or user changes

  const sizeClasses = {
    xs: "w-6 h-6",
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16 sm:w-20 sm:h-20",
    xl: "w-20 h-20",
  };

  const iconSizes = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
    xl: "w-10 h-10",
  };

  // Generate initials from user data
  const getInitials = () => {
    const name =
      profileData?.displayName ||
      user?.displayName ||
      currentUser?.displayName ||
      "";
    if (name) {
      return name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return "U";
  };

  const defaultFallback =
    shouldShowFallback &&
    (profileData?.displayName ||
      user?.displayName ||
      currentUser?.displayName) ? (
      <div
        className={`${iconSizes[size]} text-gray-600 font-semibold flex items-center justify-center`}
      >
        {getInitials()}
      </div>
    ) : (
      <User className={`${iconSizes[size]} text-gray-400`} />
    );

  // Handle file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !currentUser) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    try {
      setUploading(true);
      setShowUploadMenu(false);

      // Upload to Firebase Storage
      const { userService } = await import("../services/firebaseConfig");
      const photoURL = await userService.uploadProfilePhoto(
        currentUser.uid,
        file
      );

      if (photoURL) {
        // Update Firestore Users/{uid} document
        const { doc, updateDoc, serverTimestamp } = await import("firebase/firestore");
        const { db } = await import("../services/firebaseConfig");
        const userRef = doc(db, "Users", currentUser.uid);
        await updateDoc(userRef, {
          profileImage: photoURL,
          photoURL: photoURL,
          updatedAt: serverTimestamp(),
        });

        // Update Firebase Auth profile
        const { updateProfile } = await import("firebase/auth");
        const { auth } = await import("../services/firebaseConfig");
        if (auth.currentUser) {
          await updateProfile(auth.currentUser, { photoURL });
        }

        // Also update via userService for consistency
        await userService.updateUserProfile(currentUser.uid, {
          photoURL: photoURL,
        });

        // Update local state
        setProfileData((prev: any) =>
          prev ? { ...prev, photoURL: photoURL } : null
        );

        // Call callback if provided
        if (onPhotoUpdate) {
          onPhotoUpdate(sanitizeString(photoURL, 500));
        }

        toast.success("Profile picture updated successfully!");

        // Refresh the page to update all instances
        window.location.reload();
      }
    } catch (error) {
      // SECURITY: Removed console.error to prevent information leakage
      alert("Failed to upload profile photo. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Handle remove photo
  const handleRemovePhoto = async () => {
    try {
      if (!currentUser) return;

      // Rate limiting check
      const rateLimitCheck = profilePictureRateLimiter.isAllowed(currentUser.uid);
      if (!rateLimitCheck.allowed) {
        alert(rateLimitCheck.error || 'Rate limit exceeded');
        return;
      }

      setUploading(true);
      setShowUploadMenu(false);

      // Delete from Firebase Storage
      const { userService } = await import("../services/firebaseConfig");
      await userService.deleteProfilePhoto(currentUser.uid);

      // Update Firestore Users/{uid} document
      const { doc, updateDoc, serverTimestamp } = await import("firebase/firestore");
      const { db } = await import("../services/firebaseConfig");
      const userRef = doc(db, "Users", currentUser.uid);
      await updateDoc(userRef, {
        profileImage: null,
        photoURL: null,
        updatedAt: serverTimestamp(),
      });

      // Update Firebase Auth profile
      const { updateProfile } = await import("firebase/auth");
      const { auth } = await import("../services/firebaseConfig");
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { photoURL: null });
      }

      // Also update via userService for consistency
      await userService.updateUserProfile(currentUser.uid, {
        photoURL: null,
      });

      // Update local state
      setProfileData((prev: any) =>
        prev ? { ...prev, photoURL: undefined } : null
      );

      // Call callback if provided
      if (onPhotoUpdate) {
        onPhotoUpdate("");
      }

      toast.success("Profile picture removed successfully!");

      // Refresh the page to update all instances
      window.location.reload();
    } catch (error) {
      // SECURITY: Removed console.error to prevent information leakage
      alert("Failed to remove profile photo. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="relative group">
      <div
        className={`${
          sizeClasses[size]
        } bg-gray-100 rounded-full flex items-center justify-center overflow-hidden ${className} ${
          editable && isCurrentUser
            ? "cursor-pointer hover:bg-gray-200 transition-colors"
            : ""
        }`}
      >
        {uploading ? (
          <CircularSkeleton size="sm" className={iconSizes[size]} />
        ) : imageSrc && !shouldShowFallback ? (
          <img
            src={imageSrc}
            alt={sanitizeString(alt || 'Profile', 100)}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            width={48}
            height={48}
            referrerPolicy="no-referrer"
            crossOrigin={imageSrc && (imageSrc.includes('firebasestorage.googleapis.com') || imageSrc.includes('firebasestorage.app')) ? "anonymous" : undefined}
            onError={(e) => {
              // If image fails to load, set error state and show fallback
              // SECURITY: Removed console.warn to prevent information leakage
              const target = e.target as HTMLImageElement;
              if (target) {
                // Log more detailed error information
                const errorInfo = {
                  src: imageSrc,
                  naturalWidth: target.naturalWidth,
                  naturalHeight: target.naturalHeight,
                  complete: target.complete,
                };
                // SECURITY: Removed console.warn to prevent information leakage
                
                // Check for specific error types
                if (target.naturalWidth === 0 && target.naturalHeight === 0) {
                  // SECURITY: Removed console.warn to prevent information leakage
                }
              }
              setImageLoadError(true);
            }}
            onLoad={() => {
              // Reset error state if image loads successfully
              setImageLoadError(false);
            }}
          />
        ) : (
          fallbackIcon || defaultFallback
        )}
      </div>

      {/* Upload overlay for editable profile pictures */}
      {editable && isCurrentUser && !uploading && (
        <div
          className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          onClick={() => setShowUploadMenu(true)}
        >
          <Camera className={`${iconSizes[size]} text-white`} />
        </div>
      )}

      {/* Upload menu - responsive modal */}
      {showUploadMenu && editable && isCurrentUser && (
        <>
          {/* Backdrop - close on click outside (mobile) */}
          <div
            className="fixed inset-0 bg-black bg-opacity-20 z-40 md:hidden"
            onClick={() => setShowUploadMenu(false)}
          />
          
          {/* Modal - responsive positioning */}
          <div className="
            fixed left-4 right-4 top-1/2 -translate-y-1/2
            md:absolute md:left-1/2 md:right-auto md:top-full md:-translate-x-1/2 md:translate-y-0 md:mt-2
            bg-white rounded-lg shadow-xl border border-gray-200 p-2 
            min-w-[240px] sm:min-w-[280px] 
            max-w-[calc(100vw-2rem)] md:max-w-none
            z-50
            overflow-hidden
          ">
            <div className="flex items-center justify-between p-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-900">
                Update Photo
              </span>
              <button
                onClick={() => setShowUploadMenu(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="p-2 space-y-2">
              <label className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <Upload className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">Upload Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>

              {imageSrc && (
                <button
                  onClick={handleRemovePhoto}
                  className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-red-50 transition-colors text-red-600"
                >
                  <X className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">Remove Photo</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Uploading indicator */}
      {uploading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
          <div className="flex items-center space-x-2 text-white">
            <CircularSkeleton size="sm" className="text-white" />
            <span className="text-xs">Uploading...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePicture;
